import { AnimatePresence, motion } from "motion/react";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import {
  FieldConfig,
  FieldType,
  useFieldConfigStore,
} from "../../stores/fieldConfigStore";

interface FieldConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  container?: HTMLElement;
}

// Icons for the different field types
const FieldTypeIcon = ({ type }: { type: FieldType["type"] }) => {
  switch (type) {
    case "date":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      );
    case "money":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
          />
        </svg>
      );
    case "area":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
          />
        </svg>
      );
    case "percentage":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    case "number":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
          />
        </svg>
      );
    case "email":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    case "phone":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
      );
    case "boolean":
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h7"
          />
        </svg>
      );
  }
};

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const ExportIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const ImportIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v6m0 0l-3-3m3 3l3-3M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

// Component to configure an individual field
const FieldConfigRow: React.FC<{
  config: FieldConfig;
  onUpdate: (updates: Partial<FieldConfig>) => void;
}> = ({ config, onUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const fieldTypeOptions = [
    { value: "text", label: "Text" },
    { value: "number", label: "Number" },
    { value: "date", label: "Date" },
    { value: "money", label: "Money" },
    { value: "area", label: "Area" },
    { value: "percentage", label: "Percentage" },
    { value: "boolean", label: "Boolean" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
  ] as const;

  const getDefaultFormat = (type: FieldType["type"]): string => {
    switch (type) {
      case "date":
        return "MM/DD/YYYY";
      case "money":
        return "$";
      case "area":
        return "sqft";
      case "percentage":
        return "%";
      default:
        return "";
    }
  };

  const getDefaultUnit = (type: FieldType["type"]): string => {
    switch (type) {
      case "money":
        return "USD";
      case "area":
        return "square feet";
      default:
        return "";
    }
  };

  const handleTypeChange = (newType: FieldType["type"]) => {
    const newFieldType: FieldType = {
      type: newType,
      format: getDefaultFormat(newType),
      unit: getDefaultUnit(newType),
      description: config.fieldType.description,
    };
    onUpdate({ fieldType: newFieldType });
  };

  return (
    <div className="border border-gray-200 rounded-lg mb-2">
      <div
        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3 flex-1">
          <input
            type="checkbox"
            checked={config.isActive}
            onChange={(e) => {
              e.stopPropagation();
              onUpdate({ isActive: e.target.checked });
            }}
            className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
          />
          <div className="flex items-center space-x-2">
            <FieldTypeIcon type={config.fieldType.type} />
            <span className="text-sm font-medium text-gray-900">
              {config.displayName}
            </span>
          </div>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
            {config.fieldType.type}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-500">{config.fieldPath}</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-gray-200 p-4 bg-gray-50"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data type
                </label>
                <select
                  value={config.fieldType.type}
                  onChange={(e) =>
                    handleTypeChange(e.target.value as FieldType["type"])
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  {fieldTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {(config.fieldType.type === "date" ||
                config.fieldType.type === "money" ||
                config.fieldType.type === "area" ||
                config.fieldType.type === "percentage") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Format
                  </label>
                  <input
                    type="text"
                    value={config.fieldType.format || ""}
                    onChange={(e) =>
                      onUpdate({
                        fieldType: {
                          ...config.fieldType,
                          format: e.target.value,
                        },
                      })
                    }
                    placeholder={getDefaultFormat(config.fieldType.type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}

              {(config.fieldType.type === "money" ||
                config.fieldType.type === "area") && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={config.fieldType.unit || ""}
                    onChange={(e) =>
                      onUpdate({
                        fieldType: {
                          ...config.fieldType,
                          unit: e.target.value,
                        },
                      })
                    }
                    placeholder={getDefaultUnit(config.fieldType.type)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={config.fieldType.description || ""}
                  onChange={(e) =>
                    onUpdate({
                      fieldType: {
                        ...config.fieldType,
                        description: e.target.value,
                      },
                    })
                  }
                  placeholder="Field description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FieldConfigModal: React.FC<FieldConfigModalProps> = ({
  isOpen,
  onClose,
  container,
}) => {
  const {
    fieldConfigs,
    isInitialized,
    updateFieldConfig,
    resetToDefaults,
    exportConfiguration,
    importConfiguration,
  } = useFieldConfigStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");

  // Filter the configurations according to the search criteria
  const filteredConfigs = fieldConfigs.filter((config) => {
    const matchesSearch =
      config.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.fieldPath.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType =
      selectedType === "all" || config.fieldType.type === selectedType;
    return matchesSearch && matchesType;
  });

  const groupedConfigs = filteredConfigs.reduce((groups, config) => {
    const type = config.fieldType.type;
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(config);
    return groups;
  }, {} as Record<string, FieldConfig[]>);

  const handleResetToDefaults = () => {
    if (
      window.confirm(
        "Are you sure you want to reset all field parameters? This action is irreversible."
      )
    ) {
      resetToDefaults();
    }
  };

  const handleExportConfiguration = () => {
    try {
      const configData = exportConfiguration();
      const blob = new Blob([configData], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `field-config-${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Error exporting configuration");
      console.error("Export error:", error);
    }
  };

  const handleImportConfiguration = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const success = importConfiguration(content);

          if (success) {
            alert("Configuration imported successfully!");
          } else {
            alert(
              "Error importing configuration. Please check the file format."
            );
          }
        } catch (error) {
          alert("Error reading file");
          console.error("Import error:", error);
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000000001]"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] m-4 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <SettingsIcon />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                AI Chat Field Configuration
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Configure the data types and formats to improve the AI chat
                responses
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-md p-1"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Filter controls */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search for fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
            <div className="sm:w-48">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All types</option>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="money">Money</option>
                <option value="area">Area</option>
                <option value="percentage">Percentage</option>
                <option value="boolean">Boolean</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleExportConfiguration}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                title="Export configuration"
              >
                <ExportIcon />
                Export
              </button>
              <button
                onClick={handleImportConfiguration}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center gap-2"
                title="Import configuration"
              >
                <ImportIcon />
                Import
              </button>
              <button
                onClick={handleResetToDefaults}
                className="px-4 py-2 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            {filteredConfigs.length} field(s) found on {fieldConfigs.length}{" "}
            total
          </div>
        </div>

        {/* Modal body */}
        <div className="flex-1 overflow-y-auto p-6">
          {!isInitialized ? (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-4">
                <SettingsIcon />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No data available
              </h3>
              <p className="text-gray-600">
                Load some plan data first to configure the fields.
              </p>
            </div>
          ) : filteredConfigs.length === 0 ? (
            <div className="text-center py-8">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No field found
              </h3>
              <p className="text-gray-600">
                Try to modify your search criteria.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedConfigs).map(([type, configs]) => (
                <div key={type} className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center space-x-2">
                    <FieldTypeIcon type={type as FieldType["type"]} />
                    <span>{type}</span>
                    <span className="text-gray-400">({configs.length})</span>
                  </h3>
                  <div className="space-y-2">
                    {configs.map((config) => (
                      <FieldConfigRow
                        key={config.fieldPath}
                        config={config}
                        onUpdate={(updates) =>
                          updateFieldConfig(config.fieldPath, updates)
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {fieldConfigs.filter((c) => c.isActive).length} field(s) active
            </div>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>,
    container || document.body
  );
};

export default FieldConfigModal;
